/**
 * Pure builders that turn no-code UI choices into Jinja2 email-template syntax.
 * Used by EmailHtmlEditor / EmailLogicBuilderDialog; kept free of React so unit
 * tests stay simple.
 */

const IDENTIFIER_RE = /^[a-zA-Z_][a-zA-Z0-9_]*$/;

/** `true` for a safe Jinja identifier (variable / loop item name). */
export function isJinjaIdentifier(name) {
  return IDENTIFIER_RE.test(String(name || ''));
}

/**
 * Condition operators shown in the visual builder (HR-friendly labels).
 * `needsValue` means the user must enter a compare string.
 */
export const CONDITION_OPERATORS = [
  {
    value: 'present',
    label: 'Has a value',
    hint: 'Show when this field is filled in',
    needsValue: false,
  },
  {
    value: 'empty',
    label: 'Is empty or missing',
    hint: 'Show when this field has nothing',
    needsValue: false,
  },
  {
    value: 'equals',
    label: 'Equals…',
    hint: 'Exact match (e.g. Hire)',
    needsValue: true,
  },
  {
    value: 'not_equals',
    label: 'Does not equal…',
    hint: 'Anything except this exact text',
    needsValue: true,
  },
  {
    value: 'contains',
    label: 'Contains…',
    hint: 'Field text includes this phrase',
    needsValue: true,
  },
];

const LIST_NAME_RE = /(_list|_items|_questions)$/;

/** Known list payload keys (same idea as TipTap list-var detection). */
export function isListLikeVariable(name) {
  const n = String(name || '').trim().toLowerCase();
  if (!n) return false;
  if (n === 'questions') return true;
  return LIST_NAME_RE.test(n);
}

/** Default loop item name for a collection (hidden from HR UI). */
export function defaultItemVariableForList(listVariable) {
  const n = String(listVariable || '').trim().toLowerCase();
  if (n === 'questions') return 'q';
  return 'item';
}

function wrapParagraph(htmlOrText) {
  const trimmed = String(htmlOrText || '').trim();
  if (!trimmed) return '';
  if (/^<(p|div|ul|ol|h[1-6]|blockquote|table)\b/i.test(trimmed)) {
    return trimmed;
  }
  return `<p>${trimmed}</p>`;
}

/** Escape a user compare string for a Jinja single-quoted literal. */
export function escapeJinjaString(value) {
  return String(value ?? '')
    .replace(/\\/g, '\\\\')
    .replace(/'/g, "\\'");
}

/**
 * Build the boolean expression inside `{% if … %}` / `{% elif … %}`.
 * @param {{ variable: string, operator?: string, compareValue?: string }} opts
 */
export function buildConditionExpression({
  variable,
  operator = 'present',
  compareValue = '',
}) {
  const name = String(variable || '').trim();
  if (!isJinjaIdentifier(name)) {
    throw new Error('Choose a field name like join_url or questions');
  }
  const op = String(operator || 'present');
  const raw = String(compareValue ?? '');
  if ((op === 'equals' || op === 'not_equals' || op === 'contains') && !raw.trim()) {
    throw new Error('Enter the text to compare against');
  }
  const lit = `'${escapeJinjaString(raw)}'`;
  switch (op) {
    case 'empty':
      return `not ${name}`;
    case 'equals':
      return `${name} == ${lit}`;
    case 'not_equals':
      return `${name} != ${lit}`;
    case 'contains':
      return `${lit} in ${name}`;
    case 'present':
    default:
      return name;
  }
}

/**
 * `{% if variable %}then{% else %}else{% endif %}` (else optional).
 * Kept for list wrappers and simple call sites.
 * @param {{ variable: string, thenHtml?: string, elseHtml?: string }} opts
 */
export function buildIfBlock({ variable, thenHtml = '', elseHtml = '' }) {
  const name = String(variable || '').trim();
  if (!isJinjaIdentifier(name)) {
    throw new Error('Variable must be a simple name like join_url or questions');
  }
  const thenPart = String(thenHtml || '').trim() || `<p>{{ ${name} }}</p>`;
  const elsePart = String(elseHtml || '').trim();
  if (elsePart) {
    return `{% if ${name} %}${thenPart}{% else %}${wrapParagraph(elsePart)}{% endif %}`;
  }
  return `{% if ${name} %}${thenPart}{% endif %}`;
}

/**
 * If → Else if… → Else chain from the visual builder.
 * @param {{
 *   branches: Array<{
 *     variable: string,
 *     operator?: string,
 *     compareValue?: string,
 *     thenHtml?: string,
 *   }>,
 *   elseHtml?: string,
 * }} opts
 */
export function buildConditionChain({ branches, elseHtml = '' }) {
  const list = Array.isArray(branches) ? branches.filter(Boolean) : [];
  if (!list.length) {
    throw new Error('Add at least one condition');
  }
  let out = '';
  list.forEach((branch, index) => {
    const expr = buildConditionExpression(branch);
    const keyword = index === 0 ? 'if' : 'elif';
    const name = String(branch.variable || '').trim();
    const thenPart = String(branch.thenHtml || '').trim()
      || defaultThenHtmlForVariable(name);
    out += `{% ${keyword} ${expr} %}${thenPart}`;
  });
  const elsePart = String(elseHtml || '').trim();
  if (elsePart) {
    out += `{% else %}${wrapParagraph(elsePart)}`;
  }
  out += '{% endif %}';
  return out;
}

/**
 * Loop body for a list variable.
 *
 * Emits TipTap-safe paragraph markup (never `{% for %}` inside `<ol>`/`<ul>` —
 * TipTap turns those tags into bogus list items). `fromEditorHtml` restores a
 * real `<ol>`/`<ul>` on save for email clients.
 *
 * @param {{
 *   listVariable: string,
 *   itemVariable?: string,
 *   format?: 'ol' | 'ul' | 'plain',
 * }} opts
 */
export function buildForBlock({
  listVariable,
  itemVariable = 'item',
  format = 'ol',
}) {
  const list = String(listVariable || '').trim();
  const item = String(itemVariable || 'item').trim();
  if (!isJinjaIdentifier(list)) {
    throw new Error('List variable must be a simple name like questions');
  }
  if (!isJinjaIdentifier(item)) {
    throw new Error('Item name must be a simple identifier like q or item');
  }
  if (format === 'ul') {
    return `{% for ${item} in ${list} %}<p>• {{ ${item} }}</p>{% endfor %}`;
  }
  if (format === 'plain') {
    return `{% for ${item} in ${list} %}<p>{{ ${item} }}</p>{% endfor %}`;
  }
  // Numbered: loop.index so the editor preview is readable before canonicalize.
  return `{% for ${item} in ${list} %}<p>{{ loop.index }}. {{ ${item} }}</p>{% endfor %}`;
}

/**
 * Common briefing pattern: show a list when present, optional empty message.
 * @param {{
 *   listVariable: string,
 *   itemVariable?: string,
 *   format?: 'ol' | 'ul' | 'plain',
 *   emptyHtml?: string,
 *   wrapInIf?: boolean,
 * }} opts
 */
export function buildListLogicBlock({
  listVariable,
  itemVariable = 'item',
  format = 'ol',
  emptyHtml = '',
  wrapInIf = true,
}) {
  const item = itemVariable || defaultItemVariableForList(listVariable);
  const loop = buildForBlock({ listVariable, itemVariable: item, format });
  if (!wrapInIf && !emptyHtml) return loop;
  return buildIfBlock({
    variable: listVariable,
    thenHtml: loop,
    elseHtml: emptyHtml ? wrapParagraph(emptyHtml) : '',
  });
}

/**
 * Build the HTML fragment for a condition using either the editor selection
 * or a default snippet for the chosen variable.
 */
export function defaultThenHtmlForVariable(variable, selectedHtml = '') {
  const name = String(variable || '').trim();
  const selected = String(selectedHtml || '').trim();
  if (selected) return selected;
  if (!isJinjaIdentifier(name)) return '<p></p>';
  if (isListLikeVariable(name)) {
    return buildForBlock({
      listVariable: name,
      itemVariable: defaultItemVariableForList(name),
      format: 'ol',
    });
  }
  const lower = name.toLowerCase();
  if (/(^|_)(url|uri|link|href)$/.test(lower) || lower.includes('_url')) {
    return `<p><a href="{{ ${name} }}">{{ ${name} }}</a></p>`;
  }
  return `<p>{{ ${name} }}</p>`;
}
